/**
 * Sentry instrumentation helpers for chatbot observability.
 *
 * Provides lightweight wrappers around Sentry APIs so the chatbot
 * store / API layer can tag, trace, and breadcrumb every chat turn
 * without coupling business logic to the Sentry SDK surface.
 */

import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Context: set once per conversation / session
// ---------------------------------------------------------------------------

export function setChatContext(params: {
  conversationId?: string;
  agentName?: string;
  workspaceName?: string;
}) {
  if (params.conversationId) {
    Sentry.setTag('conversation_id', params.conversationId);
  }
  if (params.agentName) {
    Sentry.setTag('agent', params.agentName);
  }
  if (params.workspaceName) {
    Sentry.setTag('workspace', params.workspaceName);
  }
}

export function clearChatContext() {
  Sentry.setTag('conversation_id', undefined);
  Sentry.setTag('agent', undefined);
}

// ---------------------------------------------------------------------------
// Breadcrumbs: key user / system actions
// ---------------------------------------------------------------------------

export function addChatBreadcrumb(
  action: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
) {
  Sentry.addBreadcrumb({
    category: 'chat',
    message: action,
    level,
    data,
  });
}

// ---------------------------------------------------------------------------
// Spans: instrument streaming phases
// ---------------------------------------------------------------------------

type ChatSpanOptions = {
  conversationId?: string;
  isStreaming?: boolean;
  isRetry?: boolean;
};

/**
 * Wraps `fn` in a Sentry span that tracks the full "send → response" cycle.
 * Automatically records first-token and completion timestamps as span data.
 */
export async function traceChatTurn<T>(
  name: string,
  options: ChatSpanOptions,
  fn: (span: Sentry.Span | undefined) => Promise<T>,
): Promise<T> {
  return Sentry.startSpan(
    {
      name,
      op: 'chat.turn',
      attributes: {
        'chat.conversation_id': options.conversationId ?? 'unknown',
        'chat.streaming': options.isStreaming ?? true,
        'chat.retry': options.isRetry ?? false,
      },
    },
    async (span) => fn(span),
  );
}

export function recordFirstToken(span: Sentry.Span | undefined) {
  if (!span) return;
  span.setAttribute('chat.first_token_ms', Date.now());
}

export function recordStreamComplete(span: Sentry.Span | undefined, tokenCount?: number) {
  if (!span) return;
  span.setAttribute('chat.stream_complete_ms', Date.now());
  if (tokenCount !== undefined) {
    span.setAttribute('chat.output_tokens', tokenCount);
  }
}

// ---------------------------------------------------------------------------
// Soft-failure capture (warnings, tool errors, timeouts, etc.)
// ---------------------------------------------------------------------------

export function captureChatWarning(
  message: string,
  extra?: Record<string, unknown>,
) {
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: { component: 'chatbot' },
    extra,
  });
}

export function captureChatError(
  error: unknown,
  extra?: Record<string, unknown>,
) {
  const err = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(err, {
    tags: { component: 'chatbot' },
    extra,
  });
}

// ---------------------------------------------------------------------------
// Tool instrumentation
// ---------------------------------------------------------------------------

export function addToolBreadcrumb(
  toolName: string,
  phase: 'start' | 'end',
  data?: Record<string, unknown>,
) {
  Sentry.addBreadcrumb({
    category: 'chat.tool',
    message: `${toolName} ${phase}`,
    level: phase === 'end' && data?.success === false ? 'warning' : 'info',
    data: { tool: toolName, ...data },
  });
}
